<template>
  <div class="message-box dialog-mask">
    <div class="dialog-box">
      <button
        type="button"
        class="close-button"
        :disabled="submitting"
        :aria-label="notReceiveLabel"
        @click="onNotReceive"
      >
        <span aria-hidden="true">×</span>
      </button>
      <div class="dialog-header">
        <img src="@/assets/img/email-magazine/cat-header.png" :alt="titleLabel" />
      </div>
      <div class="dialog-body">
        <h1>{{ $t("myPage.emailNotification.confirmDialog.title") }}</h1>
        <p class="description">{{ $t("myPage.emailNotification.confirmDialog.description") }}</p>
      </div>
      <footer>
        <button
          class="button receive"
          :disabled="submitting"
          @click="onReceive"
        >
          {{ $t("myPage.emailNotification.confirmDialog.receive") }}
        </button>
        <button
          class="button not-receive"
          :disabled="submitting"
          @click="onNotReceive"
        >
          {{ $t("myPage.emailNotification.confirmDialog.notReceive") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

export type EmailMagazineConfirmDialogResult = "receive" | "notReceive";

export default Vue.extend<Data, Methods, Computed, {}>({
  data() {
    return {
      submitting: false,
    };
  },
  computed: {
    notReceiveLabel() {
      return this.$t("myPage.emailNotification.confirmDialog.notReceive") as string;
    },
    titleLabel() {
      return this.$t("myPage.emailNotification.confirmDialog.title") as string;
    },
  },
  methods: {
    async onReceive() {
      if (this.submitting) return;
      this.submitting = true;
      this.$close("receive" as EmailMagazineConfirmDialogResult);
    },
    async onNotReceive() {
      if (this.submitting) return;
      this.submitting = true;
      this.$close("notReceive" as EmailMagazineConfirmDialogResult);
    },
  },
});

interface Data {
  submitting: boolean;
}

interface Methods {
  onReceive: () => Promise<void>;
  onNotReceive: () => Promise<void>;
}

interface Computed {
  notReceiveLabel: string;
  titleLabel: string;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.dialog-mask {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 9000;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-box {
  max-width: 360px;
  width: calc(100% - 32px);
  margin: 10px;
  box-sizing: border-box;
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: hidden;
  position: relative;
}

.close-button {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: var(--radius-circle);
  background-color: var(--color-surface-layer);
  color: var(--color-text-normal);
  font-size: var(--font-size-xxl);
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  box-shadow: var(--shadow-sm);
  transition: opacity 0.15s ease;

  @include hover-overlay;

  span {
    display: block;
    transform: translateY(-2px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.dialog-header {
  // 猫イラストヘッダー専用のブランド装飾色（デザイントークン化対象外）
  background-color: #d8efd1;

  img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    pointer-events: none;
  }
}

.dialog-body {
  padding: 20px 24px 8px;
  text-align: center;

  h1 {
    margin: 0 0 12px;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
  }

  .description {
    margin: 0;
    font-size: var(--font-size-sm);
    line-height: 1.6;
    color: var(--color-text-normal);
    white-space: pre-line;
  }
}

footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 16px 24px 24px;
}

.button {
  position: relative;
  padding: 12px 20px;
  border: none;
  border-radius: var(--radius-rounded);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  transition: opacity 0.15s ease;

  & + & {
    margin-top: -1px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.button.receive {
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);

  @include hover-overlay;
}

.button.not-receive {
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);

  @include hover-overlay;
}
</style>
